/**
 * Las dos ganancias del local, separadas, y el dia por dia que las arma.
 *
 * POR QUE NO SE SUMAN EN UN SOLO NÚMERO
 * -------------------------------------
 * El local gana de dos formas y no son la misma cosa.
 *
 * El MARGEN OPERATIVO es lo que deja vender: precio menos costo, con el costo
 * congelado en el comprobante. Es repetible. Si mañana se vende lo mismo, da lo
 * mismo.
 *
 * El RESULTADO FINANCIERO es lo que deja la brecha entre el dólar al que se
 * paga y el dólar al que se vende: cambiar pesos por billetes más baratos, y
 * pagarle al proveedor a una cotización menor que la de venta. No es repetible:
 * depende de la brecha del día, y puede desaparecer sin que nadie haya hecho
 * nada distinto.
 *
 * Sumarlos hace que el negocio parezca mejor de lo que es. Por eso acá van en
 * dos columnas, y el total está al final y no arriba.
 */
import { Importe } from "@/components/money";
import { Card } from "@/components/ui/card";
import { Kpi, type KpiProps } from "@/components/ui/kpi";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { formatearFecha } from "@/lib/utils";
import { aNumero, type FilaResultadoNegocio } from "@/lib/tipos";

/** Suma una columna de la vista, ya convertida a número. */
function total(
  filas: ReadonlyArray<FilaResultadoNegocio>,
  campo: keyof FilaResultadoNegocio,
): number {
  return filas.reduce((a, f) => a + aNumero(f[campo] as string | number | null), 0);
}

/**
 * Un resultado negativo no cambia el color del numero (sigue siendo pesos):
 * se dice con un badge al lado (MASTER seccion 3.2).
 */
function estadoDe(monto: number): KpiProps["estado"] {
  return monto < 0 ? { texto: "Pérdida", variant: "destructive" } : undefined;
}

/**
 * Los dos numeros del periodo, arriba de todo.
 *
 * NO hay un tercer KPI con la suma, y es a proposito: sumarlos hace que el
 * negocio parezca mejor de lo que es. El total existe, pero al pie de la tabla,
 * que es donde se lee despues de haber visto de que esta hecho.
 */
export function KpisGanancia({
  filas,
}: {
  filas: ReadonlyArray<FilaResultadoNegocio>;
}): JSX.Element {
  const operativo = total(filas, "margen_operativo_ars");
  const financiero = total(filas, "resultado_financiero_ars");
  const caja = total(filas, "arbitraje_caja_ars");
  const compras = total(filas, "arbitraje_compras_ars");
  const gastos = total(filas, "gastos_ars");
  const retiros = total(filas, "retiros_ars");
  const diferencias = total(filas, "diferencias_caja_ars");
  const otros = total(filas, "otros_ingresos_ars");

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Kpi
        etiqueta="Margen operativo"
        monto={operativo}
        moneda="ARS"
        estado={estadoDe(operativo)}
        nota="Precio menos costo, sobre lo facturado."
      />
      <Kpi
        etiqueta="Resultado financiero"
        monto={financiero}
        moneda="ARS"
        estado={estadoDe(financiero)}
        nota={
          <>
            <Importe monto={caja} moneda="ARS" tamano="sm" /> de cambiar divisas ·{" "}
            <Importe monto={compras} moneda="ARS" tamano="sm" /> de comprarle al proveedor a un
            dólar más barato.
          </>
        }
      />
      {/* Hasta la auditoría del 24/09/2026 los gastos no aparecían en ninguna
          parte de esta pantalla, y el "Total" del pie no los restaba: el número
          que el local usaba para decidir era el margen bruto. Estaban cargados
          en la caja desde siempre. */}
      <Kpi
        etiqueta="Gastos del local"
        monto={-gastos}
        moneda="ARS"
        estado={gastos > 0 ? { texto: "Sale", variant: "warning" } : undefined}
        nota={
          <>
            {retiros > 0 ? (
              <>
                Alquiler, sueldos e impuestos. Aparte,{" "}
                <Importe monto={retiros} moneda="ARS" tamano="sm" /> de retiros, que no son un gasto
                del negocio y no restan.
              </>
            ) : (
              "Alquiler, sueldos e impuestos. Los retiros no cuentan como gasto."
            )}
            {/* Un faltante de arqueo bajaba la caja y aca no aparecia: la caja y
                Ganancias quedaban desfasadas en ese importe (0121_3). Va en su
                renglon, no sumado a los gastos: no es alquiler ni sueldo. Decia
                «ya en el total» debajo del numero de gastos, y se leia como que
                estaba sumada ahi: esta en el Total del pie de la tabla. */}
            {Math.abs(diferencias) >= 0.01 ? (
              <span className="block">
                Diferencias de caja (arqueos y propinas):{" "}
                <Importe monto={diferencias} moneda="ARS" tamano="sm" signoExplicito />. No están en
                este número: se suman aparte en el Total del mes.
              </span>
            ) : null}
            {/* (0155) Intereses y «Otro» ingreso: plata que entra y no es venta.
                Antes un «Otro» egreso restaba y un «Otro» ingreso no sumaba. */}
            {Math.abs(otros) >= 0.01 ? (
              <span className="block">
                Intereses y otros ingresos:{" "}
                <Importe monto={otros} moneda="ARS" tamano="sm" signoExplicito />. Se suman aparte
                en el Total del mes.
              </span>
            ) : null}
          </>
        }
      />
    </div>
  );
}

/** El dia por dia: de que esta hecho cada uno de los dos numeros de arriba. */
export function PorDia({ filas }: { filas: ReadonlyArray<FilaResultadoNegocio> }): JSX.Element {
  const operativo = total(filas, "margen_operativo_ars");
  const otrosTotal = total(filas, "otros_ingresos_ars");
  const financiero = total(filas, "resultado_financiero_ars");

  return (
    <Card padding="none" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Table densidad="compacta" alto="lleno">
        <TableHeader>
          <TableRow>
            <TableHead>Día</TableHead>
            <TableHead className="text-right">Facturado ($)</TableHead>
            <TableHead className="text-right">Costo ($)</TableHead>
            <TableHead className="text-right">Margen ($)</TableHead>
            <TableHead className="text-right">Financiero ($)</TableHead>
            <TableHead className="text-right">Gastos ($)</TableHead>
            <TableHead className="text-right">Total ($)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.length === 0 ? (
            <TableVacia
              colSpan={7}
              mensaje="Todavía no hay ventas ni cambios de moneda para mostrar."
            />
          ) : (
            filas.map((f) => (
              <TableRow key={String(f.dia)}>
                <TableCell className="whitespace-nowrap">{formatearFecha(f.dia)}</TableCell>
                <TableCellImporte monto={aNumero(f.ventas_ars)} moneda="ARS" codigo={false} />
                <TableCellImporte monto={aNumero(f.costo_ars)} moneda="ARS" codigo={false} />
                <TableCellImporte
                  monto={aNumero(f.margen_operativo_ars)}
                  moneda="ARS"
                  codigo={false}
                />
                <TableCellImporte
                  monto={aNumero(f.resultado_financiero_ars)}
                  moneda="ARS"
                  codigo={false}
                />
                <CeldaGastos
                  gastos={aNumero(f.gastos_ars)}
                  diferencias={aNumero(f.diferencias_caja_ars)}
                  otros={aNumero(f.otros_ingresos_ars)}
                />
                <TableCellImporte
                  monto={aNumero(f.resultado_total_ars)}
                  moneda="ARS"
                  codigo={false}
                />
              </TableRow>
            ))
          )}
        </TableBody>
        {filas.length > 0 ? (
          <TableFooter>
            <TableRow>
              <TableCell>Total</TableCell>
              <TableCellImporte monto={total(filas, "ventas_ars")} moneda="ARS" codigo={false} />
              <TableCellImporte monto={total(filas, "costo_ars")} moneda="ARS" codigo={false} />
              <TableCellImporte monto={operativo} moneda="ARS" codigo={false} />
              <TableCellImporte monto={financiero} moneda="ARS" codigo={false} />
              <CeldaGastos
                gastos={total(filas, "gastos_ars")}
                diferencias={total(filas, "diferencias_caja_ars")}
                otros={otrosTotal}
              />
              <TableCellImporte
                monto={total(filas, "resultado_total_ars")}
                moneda="ARS"
                codigo={false}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </Card>
  );
}

/**
 * Los gastos del dia y, debajo, la diferencia de caja si la hubo (0121_3).
 *
 * El faltante de un arqueo ya esta en el Total; sin decirlo aca, el Total no se
 * explicaba con las columnas a la vista. Va debajo de los gastos y no en una
 * columna propia: una octava columna empujaba el Total fuera de la tabla.
 */
function CeldaGastos({
  gastos,
  diferencias,
  otros = 0,
}: {
  gastos: number;
  diferencias: number;
  /** Intereses y «Otro» ingreso (0155). Ya estan en el Total. */
  otros?: number;
}): JSX.Element {
  return (
    <TableCellImporte>
      <Importe monto={-gastos} moneda="ARS" codigo={false} />
      {Math.abs(diferencias) >= 0.01 ? (
        <span className="block text-xs text-muted-foreground">
          dif. de caja <Importe monto={diferencias} moneda="ARS" tamano="sm" codigo={false} />
        </span>
      ) : null}
      {Math.abs(otros) >= 0.01 ? (
        <span className="block text-xs text-muted-foreground">
          otros ingresos <Importe monto={otros} moneda="ARS" tamano="sm" codigo={false} />
        </span>
      ) : null}
    </TableCellImporte>
  );
}
